import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import type { DataClassification, TrustGrade } from '@/types/financial';

export interface WizardStepClassificationProps {
  dataClassification?: DataClassification;
  onSelect: (classification: DataClassification, trustGrade: TrustGrade) => void;
  onNext: () => void;
  onBack?: () => void; // Optional (not shown for account context)
  onCancel: () => void;
}

/**
 * Wizard Step 2: Data Classification
 *
 * Radio group with descriptions:
 * - Accountant Prepared Financials → Credit Grade
 * - Tax Return Data → Credit Grade
 * - Management Accounts / Interim → Insight Grade
 * - Xero options greyed out (Phase 3)
 *
 * Auto-sets trustGrade based on selection
 */
export const WizardStepClassification: React.FC<WizardStepClassificationProps> = ({
  dataClassification,
  onSelect,
  onNext,
  onBack,
  onCancel,
}) => {
  const handleSelect = (value: string) => {
    const classification = value as DataClassification;
    let trustGrade: TrustGrade = 'credit_grade';

    // Auto-set trust grade based on classification
    if (classification === 'management_accounts') {
      trustGrade = 'insight_grade';
    }

    onSelect(classification, trustGrade);
  };

  const isNextDisabled = !dataClassification;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold" data-testid="wizard-step-title">
          Data Classification
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Select the type of financial data you will be providing
        </p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Lenders rely on accountant-prepared financials and tax returns for credit
          assessment. Management accounts provide valuable insights but carry lower weight
          in lending decisions.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <RadioGroup value={dataClassification} onValueChange={handleSelect}>
          {/* Accountant Prepared */}
          <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
            <RadioGroupItem
              value="accountant_prepared"
              id="classification-accountant-prepared"
              data-testid="radio-classification-accountant-prepared"
            />
            <div className="flex-1">
              <Label
                htmlFor="classification-accountant-prepared"
                className="text-base font-medium cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span>Accountant Prepared Financials</span>
                  <Badge
                    variant="default"
                    className="bg-green-600"
                    data-testid="badge-trust-grade-accountant-prepared"
                  >
                    Credit Grade
                  </Badge>
                </div>
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Full financial statements prepared and reviewed by a qualified accountant
              </p>
            </div>
          </div>

          {/* Tax Return */}
          <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
            <RadioGroupItem
              value="tax_return"
              id="classification-tax-return"
              data-testid="radio-classification-tax-return"
            />
            <div className="flex-1">
              <Label
                htmlFor="classification-tax-return"
                className="text-base font-medium cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span>Tax Return Data</span>
                  <Badge
                    variant="default"
                    className="bg-green-600"
                    data-testid="badge-trust-grade-tax-return"
                  >
                    Credit Grade
                  </Badge>
                </div>
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Financial data from lodged tax returns (ATO verified)
              </p>
            </div>
          </div>

          {/* Management Accounts */}
          <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
            <RadioGroupItem
              value="management_accounts"
              id="classification-management-accounts"
              data-testid="radio-classification-management-accounts"
            />
            <div className="flex-1">
              <Label
                htmlFor="classification-management-accounts"
                className="text-base font-medium cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span>Management Accounts / Interim</span>
                  <Badge
                    variant="default"
                    className="bg-amber-600"
                    data-testid="badge-trust-grade-management-accounts"
                  >
                    Insight Grade
                  </Badge>
                </div>
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Internal financial reports (unaudited, for analysis and trend monitoring)
              </p>
            </div>
          </div>

          {/* Xero Finalised - Disabled */}
          <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4 opacity-50 cursor-not-allowed">
            <RadioGroupItem value="xero_finalised" id="classification-xero-finalised" disabled />
            <div className="flex-1">
              <Label
                htmlFor="classification-xero-finalised"
                className="text-base font-medium"
              >
                <div className="flex items-center gap-2">
                  <span>Xero (Finalised Period)</span>
                  <Badge variant="outline">Coming Sprint 10</Badge>
                </div>
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Synced from Xero with finalised year-end
              </p>
            </div>
          </div>

          {/* Xero Draft - Disabled */}
          <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4 opacity-50 cursor-not-allowed">
            <RadioGroupItem value="xero_draft" id="classification-xero-draft" disabled />
            <div className="flex-1">
              <Label htmlFor="classification-xero-draft" className="text-base font-medium">
                <div className="flex items-center gap-2">
                  <span>Xero (Draft / In Progress)</span>
                  <Badge variant="outline">Coming Sprint 10</Badge>
                </div>
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Synced from Xero (not yet finalised)
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      <div className="flex justify-between pt-4">
        <div className="flex gap-2">
          {onBack && (
            <Button variant="outline" onClick={onBack} data-testid="button-wizard-back">
              Back
            </Button>
          )}
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <Button onClick={onNext} disabled={isNextDisabled} data-testid="button-wizard-next">
          Next
        </Button>
      </div>
    </div>
  );
};
